/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { fetchBillingPortalUrl } from "../../api/queries";
import { useLanguage } from "../../contexts/LanguageContext";
import { getApiErrorMessages } from "../../utils/apiErrorReasons";
import { ErrorMessages } from "../../components/ui/Modal";
import { buttonClass, cardClass } from "../../components/ui/styles";
import { Pill } from "../../components/ui/Pill";
import { hasRunningSubscription } from "./storePayload";

const STATUS_TONE = { active: "green", trialing: "indigo", past_due: "orange" };

function planName(t, plan) {
  if (!plan) return "";
  const key = `pricing.tier.${plan}.name`;
  const label = t(key);
  return label === key ? plan : label;
}

export default function PlanSection({ store, cardCount, branchCount }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [opening, setOpening] = useState(false);
  const [portalErrors, setPortalErrors] = useState(null);

  const openPortal = async () => {
    setOpening(true);
    setPortalErrors(null);
    try {
      const response = await queryClient.fetchQuery({
        queryKey: ["billingPortal", store.id],
        queryFn: () => fetchBillingPortalUrl(store.id),
      });
      if (!response?.portal_url) throw new Error("No portal_url");
      window.location.href = response.portal_url;
    } catch (error) {
      setPortalErrors(
        getApiErrorMessages(error, t, "settings.plan.portalError"),
      );
      setOpening(false);
    }
  };

  if (!hasRunningSubscription(store)) {
    return (
      <section
        aria-labelledby="settings-plan-title"
        className={`${cardClass} flex flex-col items-start gap-3.5 p-6`}
      >
        <span className="text-[13px] text-gray-600">
          {t("settings.plan.current")}
        </span>
        <h2
          id="settings-plan-title"
          className="text-2xl font-semibold tracking-tight text-gray-900"
        >
          {t("settings.plan.none")}
        </h2>
        <p className="max-w-[520px] text-sm leading-relaxed text-gray-700">
          {t("settings.billing.noSubscription")}
        </p>
        <Link to="/pricing" className={buttonClass.primary}>
          {t("settings.plan.viewPlans")}
        </Link>
      </section>
    );
  }

  const status = store.subscription_status;
  const usage = [
    {
      label: t("settings.plan.usage.cards"),
      count: cardCount,
      limit: store.features?.pass_limit,
    },
    {
      label: t("settings.plan.usage.branches"),
      count: branchCount,
      limit: store.features?.branch_limit,
    },
  ].filter(
    (u) => Number.isFinite(u.limit) && u.limit > 0 && Number.isFinite(u.count),
  );

  return (
    <section
      aria-labelledby="settings-plan-title"
      className={`${cardClass} flex flex-col gap-[18px] p-6`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] text-gray-600">
            {t("settings.plan.current")}
          </span>
          <span className="flex flex-wrap items-center gap-2.5">
            <h2
              id="settings-plan-title"
              className="text-2xl font-semibold tracking-tight text-gray-900"
            >
              {planName(t, store.current_plan)}
            </h2>
            <Pill tone={STATUS_TONE[status]}>
              {t(`settings.plan.status.${status}`)}
            </Pill>
          </span>
        </div>
        <div className="flex max-w-[280px] flex-col items-start gap-1.5 sm:items-end sm:text-right">
          <button
            type="button"
            onClick={openPortal}
            disabled={opening}
            className={buttonClass.primary}
          >
            {t("settings.billing.manageSubscription")}
          </button>
          <span className="text-xs text-gray-600">
            {t("settings.plan.portalHint")}
          </span>
        </div>
      </div>

      <ErrorMessages messages={portalErrors} />

      {usage.length > 0 && (
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          {usage.map((u) => (
            <div
              key={u.label}
              className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3.5"
            >
              <span className="flex justify-between gap-2 text-sm">
                <span className="text-gray-700">{u.label}</span>
                <span className="font-semibold tabular-nums text-gray-900">
                  {t("settings.plan.usage.of", {
                    count: u.count,
                    limit: u.limit,
                  })}
                </span>
              </span>
              <span
                role="meter"
                aria-label={u.label}
                aria-valuemin={0}
                aria-valuemax={u.limit}
                aria-valuenow={u.count}
                className="h-2 overflow-hidden rounded-full bg-gray-100"
              >
                <span
                  className="block h-full rounded-full bg-indigo-600"
                  style={{
                    width: `${Math.min(100, (u.count / u.limit) * 100)}%`,
                  }}
                />
              </span>
            </div>
          ))}
        </div>
      )}

      <Link
        to="/pricing"
        className="self-start text-sm font-medium text-indigo-700 hover:text-indigo-800"
      >
        {t("settings.plan.compare")}
      </Link>
    </section>
  );
}
